<template>
    <div class="parametric-filters">
        <div class="parametric-filters__title">
            Подбор по параметрам
        </div>

        <form id="parametric-form"
              ref="form"
              class="parametric-filters__form"
              @submit.prevent="_changeFilters"
              @reset.prevent="_resetFilters">
            <div v-if="filters.length"
                 ref="filtersContent"
                 :class="['parametric-filter__content', {'is-full': showAllFilter}]">
                <div v-for="(filter, index) in filters"
                     :key="`${filter.category}_${index}`"
                     :class="[
                         'parametric-filter__container',
                         `parametric-filter__width-${filter.width}`,
                         {'parametric-filter__negative-margin': filter.negativeMargin}
                     ]">
                    <div class="parametric-filter__filter">
                        <template v-if="isCheckboxFilter(filter.type)">
                            <ParametricCheckbox :label="filter.label"
                                                :options="filter.items"
                                                :view-type="filter.view"
                                                :name="filter.category"
                                                @onChange="_changeFilters" />
                        </template>

                        <template v-if="isSliderFilter(filter.type)">
                            <RangeSlider
                                ref="slider"
                                :slider="filter"
                                :slider-type="filter.category"
                                @onChange="_changeFilters" />
                        </template>

                        <template v-if="isSelectFilter(filter.type)">
                            <ParametricSelect :options="filter.items"
                                              :name="filter.category"
                                              :disabled="filter.disabled"
                                              :multiple="!filter.isSingle"
                                              :label="filter.label"
                                              @onChange="_changeFilters" />
                        </template>
                        
                        <template v-if="isMasterBedroomFilter(filter.type)">
                            <div 
                                :class="[
                                    'parametric-checkbox__item',
                                    'parametric-checkbox__item-toggle',
                                    filter.disabled ? 'is-disabled' : ''
                                ]">
                                <input
                                    :id="filter.category"
                                    type="checkbox"
                                    :name="filter.category"
                                    :checked="masterBedroomState"
                                    :disabled="filter.disabled"
                                    :value="'on'"
                                    @change="masterBedroomStateHandler">
                                <label
                                    class="parametric-checkbox__item-label"
                                    :for="filter.category">
                                    <span class="parametric-checkbox__item-icon" />
                                    {{ filter.label }}
                                </label>
                            </div>
                            <ParametricCheckbox v-if="masterBedroomState"
                                                label=""
                                                :options="filter.items"
                                                :view-type="filter.view"
                                                :name="filter.category"
                                                @onChange="_changeFilters" />
                        </template>
                    </div>
                </div>
            </div>

            <div v-if="filters.length"
                 class="parametric-filters__control">
                <STBaseButton :text="toggleText"
                              icon-after="IconRightFill"
                              icon-after-modify="icon-fill icon_color_blue"
                              modify="button_theme_reset parametric-filters__toggle"
                              @click="toggleShowFiltersAll" />

                <STBaseButton text="Сбросить фильтр"
                              type="reset"
                              modify="button_theme_reset parametric-filters__reset" />
            </div>
        </form>
    </div>
</template>

<script>
import ParametricCheckbox from '@/blocks/ParametricBlock/ParametricCheckbox/ParametricCheckbox';
import ParametricSelect from '@/blocks/ParametricBlock/ParametricSelect/ParametricSelect';
import RangeSlider from '@/blocks/ParametricBlock/ParametricRangeSlider/RangeSlider';
import {mapActions} from 'vuex';

/**
 * Компонент фильтров для выборщика
 */
export default {
    name: 'ParametricFilters',

    components: {
        ParametricCheckbox,
        ParametricSelect,
        RangeSlider
    },

    props: {
        /**
         * Массив отобращаемых фильтров
         */
        filters: Array
    },

    data() {
        return {
            showAllFilter     : false, // Показываем ли все фильтры
            masterBedroomState: false // Показывать ли фильтр мастер-спальня
        };
    },

    computed: {
        /**
         * Текст кнопки для разворачивания/сворачивания фильтров
         * @return {string}
         */
        toggleText() {
            return this.showAllFilter ? 'Свернуть фильтр' : 'Развернуть фильтр';
        }
    },

    mounted() {
        this.$store.commit('parametric/SET_FORM', this.$refs.form);
    },

    methods: {
        ...mapActions('parametric', ['changeFilter']),

        /**
         * Разворачивает/сворачивает фильтры
         */
        toggleShowFiltersAll() {
            this.showAllFilter = !this.showAllFilter;

            if (this.showAllFilter && this.$refs.filtersContent) {
                const height = this.$refs.filtersContent.scrollHeight;

                this.$refs.filtersContent.style.setProperty('--filter-max-height', `${height}px`);
            }
        },

        /**
         * Событие при изменении фильтра
         * @private
         */
        _changeFilters() {
            const body = {
                type: 'filter',
                form: this.$refs.form
            };

            this.changeFilter(body)
                .then(() => {
                    this.$emit('onChangeFilters');
                });
        },

        /**
         * Событие при сбросе фильтров
         * @private
         */
        _resetFilters() {
            this.changeFilter({ type: 'reset' })
                .then(() => {
                    this.$emit('onChangeFilters');
                });
        },

        /**
         * Проверка фильтра на тип checkbox
         * @param {String} type - тип фильтра
         * @return {boolean} - является ли фильтр чекбоксом
         */
        isCheckboxFilter(type) {
            return type === 'checkbox';
        },

        /**
         * Проверка фильтра на тип slider
         * @param {String} type - тип фильтра
         * @return {boolean} - является ли фильтр рендж-слайдером
         */
        isSliderFilter(type) {
            return type === 'slider';
        },

        /**
         * Проверка фильтра на тип select
         * @param {String} type - тип фильтра
         * @return {boolean} - является ли фильтр выпадающим списком
         */
        isSelectFilter(type) {
            return type === 'select';
        },

        isMasterBedroomFilter(type) {
            return type === 'checkbox-w-title';
        },

        masterBedroomStateHandler() {
            this.masterBedroomState = !this.masterBedroomState;

            this.$nextTick(() => {
                const height = this.$refs.filtersContent.scrollHeight;
    
                this.$refs.filtersContent.style.setProperty('--filter-max-height', `${height}px`);

                this._changeFilters();
            });

        }
    }
};
</script>

<style lang="scss">
@import "ParametricFilters";
</style>
